import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import FlagFilledIcon from '@atlaskit/icon/core/flag-filled';
import FlagFilledIconOld from '@atlaskit/icon/glyph/flag-filled';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../../messages';
import {
	ADD_FLAG_BUTTON_TEST_ID,
	FLAGGED_BUTTON_TEST_ID,
	FLAG_CONTAINER_TEST_ID,
} from '../constants';

export type Props = {
	isFlagged: boolean;
	shouldRenderInlineAddFlag: boolean;
	onAddFlag: (event: MouseEvent) => void;
};

export const Flag = ({ isFlagged, shouldRenderInlineAddFlag, onAddFlag }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<FlagContainer data-testid={FLAG_CONTAINER_TEST_ID}>
			<ToolTip
				content={
					isFlagged
						? formatMessage(messages.flaggedTooltip)
						: formatMessage(messages.addFlagTooltip)
				}
			>
				{isFlagged &&
					(fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
						<FlagFilledIcon
							label="Add flag icon"
							color={token('color.icon.danger', colors.R300)}
							spacing="spacious"
							LEGACY_fallbackIcon={FlagFilledIconOld}
							LEGACY_size="small"
							LEGACY_primaryColor={token('color.icon.danger', colors.R300)}
						/>
					) : (
						<FlagFilledIconOld
							size="small"
							primaryColor={token('color.icon.danger', colors.R300)}
							label="Flagged icon"
							testId={FLAGGED_BUTTON_TEST_ID}
						/>
					))}

				{!isFlagged &&
					shouldRenderInlineAddFlag &&
					(fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
						<Box xcss={buttonContainerStyle}>
							<IconButton
								label="Add Flag"
								// eslint-disable-next-line jira/react-no-inline-function-prop
								icon={(iconProps) => (
									<FlagFilledIcon
										{...iconProps}
										label="Add flag icon"
										color={token('color.text.subtlest', colors.N200)}
										LEGACY_fallbackIcon={FlagFilledIconOld}
										LEGACY_size="small"
										LEGACY_primaryColor={token('color.text.subtlest', colors.N200)}
									/>
								)}
								appearance="subtle"
								spacing="compact"
								onClick={onAddFlag}
								testId={ADD_FLAG_BUTTON_TEST_ID}
							/>
						</Box>
					) : (
						<AddFlagButton
							aria-label="Add Flag"
							iconBefore={
								<FlagFilledIconOld
									size="small"
									primaryColor={token('color.text.subtlest', colors.N200)}
									label="Add flag icon"
								/>
							}
							spacing="compact"
							testId={ADD_FLAG_BUTTON_TEST_ID}
							onClick={onAddFlag}
						/>
					))}
			</ToolTip>
		</FlagContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlagContainer = styled.div({
	position: 'relative',
	zIndex: 1 /* surface the element above the interaction layer so tooltips can be triggered */,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddFlagButton = styled(Button)({
	opacity: '0',
	transition: 'opacity .35s ease',
	'&:hover': {
		opacity: '1',
		background: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `-${gridSize / 2}px`,
});

const buttonContainerStyle = xcss({
	display: 'inline-block',
	opacity: '0',
	transition: 'opacity .35s ease',
	':hover': {
		opacity: '1',
		background: 'none',
	},
});
